import { createContext, useContext, useMemo, type ReactNode } from "react";

import type { V2ResourceToPermission } from "./types-v2";
import {
  type ResourcePermissionInput,
  useResourcePermission,
} from "./use-resource-permission";

type Props<V2Resource extends V2ResourceToPermission> = {
  children: ReactNode;
  fallback?: ReactNode;
  permission: ResourcePermissionInput<V2Resource>;
};

export type PermissionContextType<V2Resource extends V2ResourceToPermission> = {
  permission: ResourcePermissionInput<V2Resource>;
  unauthorized: boolean;
};

const defaultContextValue = {
  permission: {} as ResourcePermissionInput<V2ResourceToPermission>,
  unauthorized: false,
};

export const PermissionContext =
  createContext<PermissionContextType<V2ResourceToPermission>>(
    defaultContextValue,
  );
PermissionContext.displayName = "PermissionContext";

export const usePermissionContext = () => useContext(PermissionContext);

export function PermissionProvider<V2Resource extends V2ResourceToPermission>({
  children,
  permission,
}: Readonly<Props<V2Resource>>) {
  const { isLoading, isPermitted } = useResourcePermission(permission);
  const unauthorized = isLoading ? true : !isPermitted;

  const value = useMemo(
    () => ({ permission, unauthorized }),
    [permission, unauthorized],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}
